import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { endpoints } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/utils-format";
import {
  DollarSign, Briefcase, FileText, AlertCircle, CheckCircle2, Users, Truck,
  Plus, ArrowUpRight, Clock, Activity,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const StatCard = ({ icon: Icon, label, value, sub, tone = "primary", testid }) => (
  <div className="stat-card" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`size-10 rounded-lg flex items-center justify-center bg-${tone}/10 text-${tone}`}>
        <Icon className="size-5" />
      </div>
    </div>
    <div className="font-display text-3xl font-bold mt-3 tracking-tight">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [jobs, setJobs] = useState([]);

  const load = async () => {
    const [s, a, j] = await Promise.all([
      endpoints.dashboard(),
      endpoints.activities(),
      endpoints.list("jobs"),
    ]);
    setStats(s.data);
    setActivities(a.data);
    setJobs(j.data);
  };

  useEffect(() => { load(); }, []);

  const upcoming = jobs
    .filter((j) => j.status !== "Completed" && j.status !== "Cancelled")
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
    .slice(0, 5);

  return (
    <Layout title="Dashboard" subtitle="Overview of your operations today">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
        <StatCard icon={DollarSign} label="Monthly Revenue" value={fmtMoney(stats?.monthly_revenue || 0)} sub={<><ArrowUpRight className="size-3 text-secondary"/> Tracking the month</>} testid="stat-revenue" />
        <StatCard icon={Briefcase} label="Jobs Today" value={stats?.jobs_today ?? "—"} sub="Scheduled for today" testid="stat-jobs-today" />
        <StatCard icon={FileText} label="Pending Estimates" value={stats?.pending_estimates ?? "—"} sub="Awaiting approval" testid="stat-pending-estimates" />
        <StatCard icon={AlertCircle} label="Pending Payments" value={fmtMoney(stats?.pending_payments || 0)} sub="Outstanding balance" testid="stat-pending-payments" />
        <StatCard icon={CheckCircle2} label="Completed Jobs" value={stats?.completed_jobs ?? "—"} sub="All-time" testid="stat-completed" />
        <StatCard icon={Users} label="Active Customers" value={stats?.active_customers ?? "—"} sub="On the books" testid="stat-customers" />
        <StatCard icon={Truck} label="Active Crews" value={stats?.active_crews ?? "—"} sub="Working today" testid="stat-crews" />
        <div className="stat-card" data-testid="quick-actions">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Quick Actions</div>
          <div className="grid grid-cols-2 gap-2">
            <Link to="/clients?new=1"><Button size="sm" variant="outline" className="w-full justify-start" data-testid="qa-add-client"><Plus className="size-3.5 mr-1"/>Add Client</Button></Link>
            <Link to="/estimates?new=1"><Button size="sm" variant="outline" className="w-full justify-start" data-testid="qa-create-estimate"><Plus className="size-3.5 mr-1"/>Estimate</Button></Link>
            <Link to="/jobs?new=1"><Button size="sm" variant="outline" className="w-full justify-start" data-testid="qa-schedule-job"><Plus className="size-3.5 mr-1"/>Schedule</Button></Link>
            <Link to="/invoices?new=1"><Button size="sm" variant="outline" className="w-full justify-start" data-testid="qa-create-invoice"><Plus className="size-3.5 mr-1"/>Invoice</Button></Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Card className="lg:col-span-2 p-6 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="font-display text-lg font-semibold">Revenue Trend</div>
              <div className="text-xs text-muted-foreground">Last 6 months</div>
            </div>
            <div className="text-xs px-2 py-1 rounded-md bg-secondary/15 text-secondary border border-secondary/30">
              +12.4% MoM
            </div>
          </div>
          <div className="h-64 mt-4" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats?.revenue_chart || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v)=>`$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.75rem", fontSize: "12px" }}
                  formatter={(v) => fmtMoney(v)}
                />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-lg font-semibold flex items-center gap-2"><Activity className="size-4 text-primary"/>Recent Activity</div>
          </div>
          <div className="space-y-3 max-h-72 overflow-auto pr-1" data-testid="recent-activity">
            {activities.length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
            {activities.slice(0, 10).map((a) => (
              <div key={a.id} className="flex gap-3 text-sm">
                <div className="size-2 rounded-full bg-primary mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{a.message}</div>
                  <div className="text-[11px] text-muted-foreground">{fmtDate(a.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6 rounded-xl mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-semibold flex items-center gap-2"><Clock className="size-4 text-primary"/>Upcoming Schedule</div>
          <Link to="/calendar" className="text-xs text-primary font-semibold hover:underline">View calendar →</Link>
        </div>
        <div className="overflow-x-auto" data-testid="upcoming-schedule">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground text-left border-b border-border">
                <th className="py-2 font-semibold">Date</th>
                <th className="font-semibold">Customer</th>
                <th className="font-semibold">Service</th>
                <th className="font-semibold">Crew</th>
                <th className="font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.length === 0 && (
                <tr><td colSpan="5" className="py-6 text-center text-muted-foreground">No upcoming jobs.</td></tr>
              )}
              {upcoming.map((j) => (
                <tr key={j.id} className="border-b border-border hover:bg-muted/40">
                  <td className="py-3 pr-4 font-medium">{fmtDate(j.date)} · {j.time}</td>
                  <td className="pr-4">{j.client_name}</td>
                  <td className="pr-4">{j.service}</td>
                  <td className="pr-4">{j.crew}</td>
                  <td><StatusPill status={j.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Layout>
  );
}
